import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  Alert,
  InputAdornment,
  IconButton,
  Divider,
  CircularProgress,
  Fade,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import LockIcon from '@mui/icons-material/Lock';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import LoginIcon from '@mui/icons-material/Login';
import { msalInstance, loginRequest, useRedirectFlow } from '../config/msal';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import { API_ENDPOINTS } from '../constants/api';
import { getApiErrorMessage } from '../utils/apiError';
import { genericCopy } from '../utils/userFacingCopy';

interface AuthConfig {
  allowPasswordLogin: boolean;
  allowMicrosoftLogin: boolean;
}

export default function LoginPage() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [authConfig, setAuthConfig] = useState<AuthConfig | null>(null);
  const [configLoading, setConfigLoading] = useState(true);
  const [configError, setConfigError] = useState(false);
  const [logoError, setLogoError] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { login, loginWithMicrosoftToken, user } = useAuth();
  const returnPath = (location.state as { from?: string } | null)?.from ?? '/';

  useEffect(() => {
    if (user) {
      navigate(returnPath, { replace: true });
      return;
    }
  }, [user, navigate, returnPath]);

  const fetchAuthConfig = async () => {
    try {
      setConfigLoading(true);
      setConfigError(false);
      const response = await apiService.get<AuthConfig>(API_ENDPOINTS.AUTH.CONFIG);
      setAuthConfig(response.data);
    } catch {
      setConfigError(true);
      setAuthConfig(null);
    } finally {
      setConfigLoading(false);
    }
  };

  useEffect(() => {
    fetchAuthConfig();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!username || !password) {
      setError('Please enter both username and password');
      return;
    }

    setError('');
    setLoading(true);

    try {
      await login(username, password);
      navigate(returnPath, { replace: true });
    } catch (err: unknown) {
      console.error('Login error:', err);
      setError(getApiErrorMessage(err, genericCopy.invalidLogin));
    } finally {
      setLoading(false);
    }
  };

  const handleMicrosoftLogin = async () => {
    try {
      setLoading(true);
      setError('');
      
      if (useRedirectFlow) {
        await msalInstance.loginRedirect(loginRequest);
        return; // navigation will occur after redirect
      }

      const response = await msalInstance.loginPopup(loginRequest);
      if (response?.account) {
        msalInstance.setActiveAccount(response.account);
      }

      // Graph access tokens from User.Read often omit email. The ID token (audience =
      // Aardvark Web) carries preferred_username / email for matching a seeded user.
      let microsoftToken = response.idToken;
      if (!microsoftToken) {
        const accounts = msalInstance.getAllAccounts();
        if (accounts.length === 0) {
          setError(genericCopy.microsoftSignInFailed);
          return;
        }
        const tokenResponse = await msalInstance.acquireTokenSilent({
          scopes: loginRequest.scopes,
          account: accounts[0],
        });
        microsoftToken = tokenResponse.idToken || tokenResponse.accessToken;
      }

      await loginWithMicrosoftToken(microsoftToken);
      navigate(returnPath, { replace: true });
    } catch (error: unknown) {
      const msalError = error as { errorCode?: string };
      if (msalError.errorCode !== 'user_cancelled') {
        setError(getApiErrorMessage(error, genericCopy.microsoftSignInFailed));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #e8ecf1 100%)',
        padding: 2,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 20% 50%, rgba(102, 126, 234, 0.05) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(118, 75, 162, 0.05) 0%, transparent 50%)',
          pointerEvents: 'none',
        },
      }}
    >
      <Fade in timeout={600}>
        <Card
          sx={{
            maxWidth: 480,
            width: '100%',
            boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
            borderRadius: 3,
            overflow: 'hidden',
            position: 'relative',
            zIndex: 1,
            bgcolor: 'white',
          }}
        >
          {/* Header Section with Subtle Accent */}
          <Box
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              p: 4,
              textAlign: 'center',
              position: 'relative',
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                height: '1px',
                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
              },
            }}
          >
<Box
              sx={{
                width: 220,
                height: 220,
                mx: 'auto',
                mb: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'visible',
              }}
            >
              {!logoError ? (
                <Box
                  component="img"
                  src="/aardvark.svg"
                  alt="Alula"
                  onError={() => setLogoError(true)}
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    transform: 'translateX(-10%)',
                  }}
                />
              ) : (
                <PersonIcon sx={{ fontSize: 48 }} />
              )}
            </Box>
            <Typography variant="h4" component="h1" fontWeight="bold" gutterBottom>
              Performance Management System
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              {!configLoading && configError
                ? 'Sign-in is temporarily unavailable'
                : !configLoading && authConfig && !authConfig.allowMicrosoftLogin && authConfig.allowPasswordLogin
                  ? 'Enter your credentials below'
                  : 'Use the options below to continue'}
            </Typography>
          </Box>

          <CardContent sx={{ p: 4 }}>
            {configLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                <CircularProgress />
              </Box>
            )}

            {!configLoading && configError && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Alert severity="warning">
                  {genericCopy.signInOptions}
                </Alert>
                <Button
                  variant="outlined"
                  onClick={fetchAuthConfig}
                  disabled={configLoading}
                  startIcon={configLoading ? <CircularProgress size={18} color="inherit" /> : undefined}
                  sx={{ alignSelf: 'center' }}
                >
                  {configLoading ? 'Retrying...' : 'Retry'}
                </Button>
              </Box>
            )}

            {!configLoading && authConfig && !configError && (
              <>
                {error && (
                  <Fade in>
                    <Alert
                      severity="error"
                      sx={{
                        mb: 3,
                        borderRadius: 2,
                        '& .MuiAlert-icon': { alignItems: 'center' },
                      }}
                      onClose={() => setError('')}
                    >
                      {error}
                    </Alert>
                  </Fade>
                )}

                {authConfig.allowPasswordLogin && (
                  <Box component="form" onSubmit={handleSubmit}>
                    <TextField
                      fullWidth
                      label="Username"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      margin="normal"
                      required={authConfig.allowPasswordLogin}
                      autoFocus={authConfig.allowPasswordLogin && !authConfig.allowMicrosoftLogin}
                      autoComplete="username"
                      disabled={loading}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PersonIcon sx={{ color: 'text.secondary' }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&:hover fieldset': { borderColor: 'primary.main' },
                        },
                      }}
                    />
                    <TextField
                      fullWidth
                      label="Password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      margin="normal"
                      required={authConfig.allowPasswordLogin}
                      autoComplete="current-password"
                      disabled={loading}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LockIcon sx={{ color: 'text.secondary' }} />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowPassword(!showPassword)}
                              edge="end"
                              disabled={loading}
                              sx={{ color: 'text.secondary' }}
                            >
                              {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '&:hover fieldset': { borderColor: 'primary.main' },
                        },
                      }}
                    />
                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      size="large"
                      disabled={loading}
                      startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <LoginIcon />}
                      sx={{
                        mt: 3,
                        mb: 2,
                        py: 1.5,
                        borderRadius: 2,
                        textTransform: 'none',
                        fontSize: '1rem',
                        fontWeight: 600,
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)',
                          boxShadow: '0 6px 20px rgba(102, 126, 234, 0.4)',
                          transform: 'translateY(-1px)',
                        },
                        '&:disabled': {
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          opacity: 0.7,
                        },
                        transition: 'all 0.2s ease-in-out',
                      }}
                    >
                      {loading ? 'Signing in...' : 'Sign In'}
                    </Button>
                  </Box>
                )}

                {authConfig.allowPasswordLogin && authConfig.allowMicrosoftLogin && (
                  <Box sx={{ mt: 2, mb: 2 }}>
                    <Divider>
                      <Typography variant="caption" color="text.secondary" sx={{ px: 2 }}>
                        Or continue with
                      </Typography>
                    </Divider>
                  </Box>
                )}

                {authConfig.allowMicrosoftLogin && (
                  <>
                    {authConfig.allowMicrosoftLogin && !authConfig.allowPasswordLogin && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
                        Use your work account
                      </Typography>
                    )}
                    <Button
                      type="button"
                      fullWidth
                      variant="outlined"
                      size="large"
                      onClick={handleMicrosoftLogin}
                      disabled={loading}
                      sx={{
                        py: 1.5,
                        borderRadius: 2,
                        textTransform: 'none',
                        fontSize: '1rem',
                        fontWeight: 600,
                        borderColor: 'divider',
                        color: 'text.primary',
                        '&:hover': {
                          borderColor: 'primary.main',
                          bgcolor: 'action.hover',
                        },
                        transition: 'all 0.2s ease-in-out',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5 }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <rect x="1" y="1" width="10" height="10" fill="#F25022"/>
                          <rect x="13" y="1" width="10" height="10" fill="#7FBA00"/>
                          <rect x="1" y="13" width="10" height="10" fill="#00A4EF"/>
                          <rect x="13" y="13" width="10" height="10" fill="#FFB900"/>
                        </svg>
                        <Typography>Continue with Microsoft</Typography>
                      </Box>
                    </Button>
                  </>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </Fade>
    </Box>
  );
}
